// Text helpers: HTML sanitising, plain text extraction, fit measurement, case changes, date formats
import { materializeTabs, layoutTabs } from './tabLayout';

const ALLOWED_TAGS = new Set(['B', 'STRONG', 'I', 'EM', 'U', 'S', 'STRIKE', 'SPAN', 'FONT', 'BR', 'DIV', 'P', 'SUP', 'SUB', 'UL', 'OL', 'LI', 'A', '#text']);
const ALLOWED_ATTRS = new Set(['style', 'color', 'face', 'size', 'href', 'target', 'rel', 'class']);
const ALLOWED_STYLE_PROPS = new Set([
  'font-family', 'font-size', 'font-weight', 'font-style', 'color', 'text-decoration', 'text-decoration-line', 'letter-spacing',
  'text-transform', 'font-variant', 'vertical-align', 'background-color', 'text-align', 'text-align-last', 'line-height', 'text-indent', 'margin-left', 'margin-right', 'padding-left',
  'margin-top', 'margin-bottom', // paragraph space before / after (Home ▸ Line Spacing, Paragraph dialog) on highlighted paragraphs
  'text-shadow', '-webkit-text-stroke', 'font-variant-ligatures', 'font-variant-numeric',
  'font-kerning', // Character Spacing ▸ Kerning on highlighted text
  '--wp-sx', // Character Spacing ▸ Scaling on highlighted text: the horizontal glyph scale of a `.wp-scale` word (see index.css)
]);
/** The only classes rich text may carry (everything else is dropped): `wp-scale` marks a horizontally scaled word. */
const ALLOWED_CLASSES = new Set(['wp-scale']);
/** Scaling factor of a `.wp-scale` word: 0.1 … 6 (10 % … 600 %, the dialog's range). */
export const SCALE_MIN = 0.1;
export const SCALE_MAX = 6;
const styleValueOk = (prop, value) => {
  if (prop === '--wp-sx') { const n = parseFloat(value); return Number.isFinite(n) && n >= SCALE_MIN && n <= SCALE_MAX && /^\s*[\d.]+\s*$/.test(value); }
  return true;
};

export const sanitizeHtml = (html) => {
  if (!html) return '';
  if (typeof window === 'undefined') return html;
  const doc = new DOMParser().parseFromString(`<div>${html}</div>`, 'text/html');
  const root = doc.body.firstChild;
  const walk = (node) => {
    const children = Array.from(node.childNodes);
    for (const child of children) {
      if (child.nodeType === Node.TEXT_NODE) continue;
      if (child.nodeType !== Node.ELEMENT_NODE || !ALLOWED_TAGS.has(child.tagName)) {
        // unwrap: keep its children
        while (child.firstChild) node.insertBefore(child.firstChild, child);
        node.removeChild(child);
        continue;
      }
      for (const attr of Array.from(child.attributes)) {
        if (!ALLOWED_ATTRS.has(attr.name.toLowerCase())) child.removeAttribute(attr.name);
        else if (attr.name === 'href' && /^\s*javascript:/i.test(attr.value)) child.removeAttribute(attr.name);
        else if (attr.name === 'class') {
          const kept = attr.value.split(/\s+/).filter((c) => ALLOWED_CLASSES.has(c));
          if (kept.length) child.setAttribute('class', kept.join(' '));
          else child.removeAttribute('class');
        } else if (attr.name === 'style') {
          const cleaned = attr.value
            .split(';')
            .map((s) => s.trim())
            .filter((s) => {
              if (!s || /url\(|expression/i.test(s)) return false;
              const i = s.indexOf(':');
              const prop = (i < 0 ? s : s.slice(0, i)).trim().toLowerCase();
              return ALLOWED_STYLE_PROPS.has(prop) && styleValueOk(prop, i < 0 ? '' : s.slice(i + 1));
            })
            .join('; ');
          if (cleaned) child.setAttribute('style', cleaned);
          else child.removeAttribute('style');
        }
      }
      walk(child);
    }
  };
  walk(root);
  return root.innerHTML;
};

export const htmlToText = (html) => {
  if (!html) return '';
  if (typeof window === 'undefined') return html.replace(/<[^>]+>/g, '');
  const div = document.createElement('div');
  div.innerHTML = html.replace(/<br\s*\/?>/gi, '\n').replace(/<\/(div|p|li)>/gi, '\n');
  return (div.textContent || '').replace(/\n{3,}/g, '\n\n').replace(/\n$/, '');
};

export const escapeHtml = (s) =>
  String(s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

/** Scale inline px values (font-size, letter-spacing, indents) so rich text zooms with the page. */
export const scaleInlineHtml = (html, scale) => {
  if (!html || !scale || scale === 1) return html || '';
  return html.replace(/(font-size|letter-spacing|text-indent|margin-left|margin-right|margin-top|margin-bottom|padding-left|line-height)\s*:\s*(-?[\d.]+)px/gi, (m, prop, v) => `${prop}: ${Math.round(parseFloat(v) * scale * 100) / 100}px`);
};

export const textToHtml = (text) => escapeHtml(text).split('\n').map((l) => `<div>${l || '<br>'}</div>`).join('');

const PARA_PROP_CSS = { lineHeight: ['line-height'], spaceBefore: ['margin-top'], spaceAfter: ['margin-bottom'], indentLeft: ['margin-left', 'padding-left'], firstLineIndent: ['text-indent'], textAlign: ['text-align'] };

/**
 * Remove the paragraphs' own values of the given paragraph props (`lineHeight`, `spaceBefore`, …) from rich HTML, so a
 * value set on the whole box applies to every paragraph again. Returns the HTML unchanged when nothing had to go.
 */
export const clearParagraphStyles = (html, keys) => {
  if (!html || typeof window === 'undefined') return html || '';
  const props = keys.flatMap((k) => PARA_PROP_CSS[k] || []);
  if (!props.length || !props.some((c) => html.includes(c))) return html;
  const root = document.createElement('div');
  root.innerHTML = html;
  return clearParagraphStylesIn(root, keys) ? root.innerHTML : html;
};

/** Same, on a live element (the box being edited): returns true when a paragraph lost one of the values. */
export const clearParagraphStylesIn = (root, keys) => {
  if (!root) return false;
  const props = keys.flatMap((k) => PARA_PROP_CSS[k] || []);
  let changed = false;
  root.querySelectorAll('div[style], p[style], li[style]').forEach((b) => {
    props.forEach((c) => { if (b.style.getPropertyValue(c)) { b.style.removeProperty(c); changed = true; } });
    if (!b.getAttribute('style')) b.removeAttribute('style');
  });
  return changed;
};

export const changeCase = (text, mode) => {
  switch (mode) {
    case 'sentence':
      return text.toLowerCase().replace(/(^\s*\w|[.!?]\s+\w)/g, (c) => c.toUpperCase());
    case 'lower':
      return text.toLowerCase();
    case 'upper':
      return text.toUpperCase();
    case 'capitalize':
      return text.toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
    case 'toggle':
      return text.split('').map((c) => (c === c.toUpperCase() ? c.toLowerCase() : c.toUpperCase())).join('');
    default:
      return text;
  }
};

export const CHAR_SPACING_PRESETS = [
  { id: 'very-tight', label: 'Very Tight', tracking: 75 },
  { id: 'tight', label: 'Tight', tracking: 90 },
  { id: 'normal', label: 'Normal', tracking: 100 },
  { id: 'loose', label: 'Loose', tracking: 110 },
  { id: 'very-loose', label: 'Very Loose', tracking: 125 },
];

/**
 * Tracking percentage (100 = normal) -> letter-spacing in px for a font size. Publisher's presets (Very Tight 75 % …
 * Very Loose 125 %) change the spacing by that share of the average glyph advance (≈ ½ em): Very Loose adds ⅛ em.
 */
export const trackingToLetterSpacing = (trackingPct, fontSize) => ((trackingPct - 100) / 100) * fontSize * 0.5;

export const LINE_SPACING_PRESETS = [1.0, 1.5, 2.0, 2.5, 3.0];

/** Measure whether html/text fits in the given box using an offscreen clone of the styles */
let measureEl = null;
const getMeasureEl = () => {
  if (!measureEl) {
    measureEl = document.createElement('div');
    measureEl.setAttribute('aria-hidden', 'true');
    measureEl.style.position = 'fixed';
    measureEl.style.left = '-99999px';
    measureEl.style.top = '0';
    measureEl.style.visibility = 'hidden';
    measureEl.style.pointerEvents = 'none';
    measureEl.style.boxSizing = 'content-box';
    document.body.appendChild(measureEl);
  }
  return measureEl;
};

/**
 * Height of `html` laid out at `width` with the given styles. `tabs` (see lib/tabLayout.js tabOptionsOf) makes tab
 * characters honour the box's tab stops, as the canvas does. The element mirrors the display rendering of a text box
 * (TextContent): the `.wp-text` rules (list margins), `.wp-para` paragraph spacing via the `--wp-sb` / `--wp-sa`
 * custom properties, `lang` for hyphenation and the wrapper element the paragraphs sit in — so what is measured here
 * is what the box shows, which is what the split of a linked story depends on.
 */
export const measureContentHeight = (styleObj, html, width, tabs = null) => {
  const el = getMeasureEl();
  el.removeAttribute('style');
  el.style.position = 'fixed';
  el.style.left = '-99999px';
  el.style.top = '0';
  el.style.visibility = 'hidden';
  el.style.pointerEvents = 'none';
  // `width` is the width the text itself gets: the paragraph indents a style may carry as padding come on top of it,
  // as they do on the canvas (the text element is `contentW` wide, border-box, padded by the indents)
  el.style.boxSizing = 'content-box';
  el.style.height = 'auto';
  el.style.width = `${width}px`;
  const spaced = parseFloat(styleObj['--wp-sb']) > 0 || parseFloat(styleObj['--wp-sa']) > 0;
  el.className = spaced ? 'wp-text wp-para' : 'wp-text';
  el.setAttribute('lang', 'en');
  Object.entries(styleObj).forEach(([k, v]) => {
    if (v === undefined || v === null || k === 'height' || k === 'overflow' || k === 'width') return;
    if (k.startsWith('--')) { el.style.setProperty(k, String(v)); return; }
    try { el.style[k] = typeof v === 'number' && !['lineHeight', 'opacity', 'columnCount', 'zIndex', 'fontWeight'].includes(k) ? `${v}px` : v; } catch { /* ignore */ }
  });
  el.innerHTML = `<div>${html}</div>`;
  if (tabs && typeof html === 'string' && html.includes('\t')) {
    materializeTabs(el);
    layoutTabs(el, tabs);
  }
  // The height the text really occupies: up to the bottom of its last line. scrollHeight would add the last
  // paragraph's space-after (its margin-bottom) — empty space below the text that Publisher never counts against the
  // box — and a line that visibly fits would be sent to the next linked box or flagged as overflow. The first
  // paragraph's space-before (collapsed onto the wrapper) is above the text and does count.
  const wrap = el.firstElementChild;
  if (!wrap) return el.scrollHeight;
  const top = el.getBoundingClientRect().top;
  const bottom = wrap.getBoundingClientRect().bottom;
  return Math.max(0, bottom - top);
};

/**
 * Largest k (0 ≤ k ≤ n) for which `fits(k)` holds, `fits` being monotone (a longer prefix never fits when a shorter
 * one does not). Galloping from `guess` — the prefix the box is expected to hold — then a binary search inside the
 * bracket: every measurement lays out a prefix about the size of the box, never the whole text, so splitting a long
 * story (thousands of words) into a box that holds a hundred costs the same as splitting a short one. `fits` is called
 * at most once per k.
 */
export const fitPrefixCount = (n, fits, guess = 64) => {
  if (n <= 0) return 0;
  const memo = new Map();
  const ok = (k) => { if (!memo.has(k)) memo.set(k, fits(k)); return memo.get(k); };
  let lo = 0; // fits (by contract)
  let k = Math.max(1, Math.min(n, Math.round(guess)));
  // gallop up while the prefix fits
  while (k < n && ok(k)) { lo = k; k = Math.min(n, k * 2); }
  if (k >= n && ok(n)) return n;
  let hi = k; // does not fit (a guess that overflowed leaves the small bracket (0, guess))
  while (hi - lo > 1) { const mid = Math.floor((lo + hi) / 2); if (ok(mid)) lo = mid; else hi = mid; }
  return lo;
};

/** Items (words + separators) a box of width × height is expected to hold at `fontSize` — the gallop's starting point. */
export const fitGuess = (styleObj, width, height) => {
  const fs = parseFloat(styleObj?.fontSize) || 16;
  return Math.max(8, Math.round((width * height) / (fs * fs * 3.2)) * 2);
};

/**
 * Find how many words of `text` fit in a box of (width x height) with given styles.
 * Returns { fitText, overflowText }.
 */
export const splitTextToFit = (styleObj, text, width, height, tabs = null) => {
  const words = text.split(/(\s+)/); // keep separators
  const fits = (k) => k === 0 || measureContentHeight(styleObj, textToHtml(words.slice(0, k).join('')), width, tabs) <= height + 0.5;
  const lo = fitPrefixCount(words.length, fits, fitGuess(styleObj, width, height));
  if (lo >= words.length) return { fitText: text, overflowText: '' };
  const fitText = words.slice(0, lo).join('');
  const overflowText = words.slice(lo).join('').replace(/^\s+/, '');
  return { fitText, overflowText };
};

export const DATE_TIME_FORMATS = [
  { id: 'long', label: (d) => d.toLocaleDateString(undefined, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }) },
  { id: 'medium', label: (d) => d.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' }) },
  { id: 'short', label: (d) => d.toLocaleDateString() },
  { id: 'iso', label: (d) => d.toISOString().slice(0, 10) },
  { id: 'month-year', label: (d) => d.toLocaleDateString(undefined, { month: 'long', year: 'numeric' }) },
  { id: 'time', label: (d) => d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' }) },
  { id: 'datetime', label: (d) => d.toLocaleString() },
];

export const uid = () => (typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : 'id-' + Math.random().toString(36).slice(2));

/**
 * "Show All" (Publisher: Special Characters, Ctrl+Shift+Y). Wraps spaces, tabs, line breaks and paragraph ends in marker
 * spans so CSS can draw · → ↵ ¶ over them without changing the text itself. Screen only — StaticPage (export, print,
 * thumbnails) never calls this.
 */
export const markSpecialChars = (html) => {
  if (!html || typeof document === 'undefined') return html || '';
  const root = document.createElement('div');
  root.innerHTML = html;
  const BLOCK = /^(DIV|P|LI|H[1-6])$/;
  const mark = (cls) => { const s = document.createElement('span'); s.className = cls; s.setAttribute('aria-hidden', 'true'); s.setAttribute('data-wp-sc', '1'); return s; };
  // 1. spaces and tabs inside text nodes
  const texts = [];
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  let n;
  while ((n = walker.nextNode())) texts.push(n);
  texts.forEach((t) => {
    const v = t.nodeValue || '';
    if (!/[ \t\u00a0]/.test(v)) return;
    const frag = document.createDocumentFragment();
    let buf = '';
    const flush = () => { if (buf) { frag.appendChild(document.createTextNode(buf)); buf = ''; } };
    for (const ch of v) {
      if (ch === ' ' || ch === '\u00a0') { flush(); const m = mark(ch === ' ' ? 'wp-sc-sp' : 'wp-sc-nb'); m.textContent = ch; frag.appendChild(m); }
      else if (ch === '\t') { flush(); const m = mark('wp-sc-tab'); m.textContent = '\t'; frag.appendChild(m); }
      else buf += ch;
    }
    flush();
    t.replaceWith(frag);
  });
  // 2. line breaks: a <br> that is the only thing in its block is an empty paragraph (¶ comes from step 3)
  root.querySelectorAll('br').forEach((br) => {
    const parent = br.parentElement;
    const alone = parent && BLOCK.test(parent.tagName) && parent.childNodes.length === 1;
    if (!alone) br.before(mark('wp-sc-br'));
  });
  // 3. paragraph ends: every block without nested blocks; bare text at the root counts as one paragraph
  const blocks = [...root.querySelectorAll('div, p, li, h1, h2, h3, h4, h5, h6')].filter((b) => !b.querySelector('div, p, li, h1, h2, h3, h4, h5, h6'));
  if (blocks.length) blocks.forEach((b) => b.appendChild(mark('wp-sc-para')));
  else root.appendChild(mark('wp-sc-para'));
  return root.innerHTML;
};
